import { InputNew, Text } from '@lune-fe/lune-ui-lib'
import type { SxProps } from '@mui/material'
import { FC, ReactElement, ReactNode } from 'react'
import { FieldRenderProps } from 'react-final-form'

import ConditionalTooltip from 'components/ConditionalTooltip'
import NotAdminTooltip from 'components/NotAdminTooltip'
import { AutoFillComponentState } from 'views/CalculateEmissions/RFQ/utils/types'

type FormInputProps<T = string> = FieldRenderProps<T> & {
    disabled?: boolean
    error?: boolean | string
    autoFill?: AutoFillComponentState
    placeholder?: string
    endAdornment: ReactNode
    number: boolean
    restrictToNumbers: boolean
    highlightOnFocus?: boolean
    tooltip?: FC<{ children: ReactElement }>
    sx?: SxProps
}

export const FormInputNew = ({
    disabled,
    input: { onChange, value, type, name },
    error,
    autoFill,
    meta,
    placeholder,
    endAdornment,
    number,
    restrictToNumbers,
    highlightOnFocus,
    tooltip,
    sx,
}: FormInputProps) => {
    const validateNumber = (value: string) => {
        if (value === '' || value === '-' || Number.isFinite(Number(value))) {
            onChange(value)
        }
    }

    return (
        <ConditionalTooltip show={!!disabled} tooltip={tooltip || NotAdminTooltip}>
            <InputNew
                data-testid={`input-${name}`}
                type={type}
                wide
                highlightOnFocus={highlightOnFocus}
                placeholder={placeholder}
                error={error || (meta.touched && meta.error)}
                value={value}
                onFocus={() => autoFill?.update(false)}
                onBlur={() => {
                    // this is a workaround to handle number value emitting values of type 'number'
                    // we need to convert it onBlur because doing so on change causes issues when typing decimal numbers
                    // Number('1.0') is converted to 1, and while typing it should not be converted
                    if (number) {
                        onChange(Number(value))
                    }
                }}
                onChange={(e) => {
                    if (number || restrictToNumbers) {
                        validateNumber(e.target.value)
                    } else {
                        return onChange(e)
                    }
                }}
                disabled={disabled}
                endAdornment={<Text variant={'body2'}>{endAdornment}</Text>}
                sx={{
                    width: '100%',
                    zIndex: 0,
                    ...(autoFill?.value && {
                        backgroundColor: 'Blue50',
                        '&:hover': {
                            backgroundColor: 'Blue100',
                        },
                    }),
                    ...sx,
                }}
            />
        </ConditionalTooltip>
    )
}
